import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Calendar, ArrowLeft, Clock, User, Tag, FileText, Download } from 'lucide-react';
import PageSEO from '@/components/PageSEO';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PostCard = ({ post }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
  >
    <Link to={`/novita/${post.id}`} className="block group">
      <div className="bg-white rounded-xl border border-slate-200 p-6 hover:shadow-lg hover:border-indigo-200 transition-all">
        <div className="flex items-center gap-2 mb-3">
          <Badge variant="secondary" className="text-xs">
            <Tag className="w-3 h-3 mr-1" />
            {post.categoria}
          </Badge>
          <span className="text-xs text-slate-400 flex items-center gap-1">
            <Calendar className="w-3 h-3" />
            {new Date(post.created_at).toLocaleDateString('it-IT', { day: 'numeric', month: 'long', year: 'numeric' })}
          </span>
        </div>
        <h3 className="text-xl font-bold text-slate-900 group-hover:text-indigo-600 transition-colors mb-2">
          {post.titolo}
        </h3>
        <p className="text-slate-600 text-sm leading-relaxed line-clamp-3">
          {post.sommario}
        </p>
        <div className="mt-4 text-indigo-600 text-sm font-medium group-hover:underline">
          Leggi di più
        </div>
      </div>
    </Link>
  </motion.div>
);

const PostList = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const { t } = useTranslation();

  useEffect(() => {
    axios.get(`${API}/blog`)
      .then(res => setPosts(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const categories = [...new Set(posts.map(p => p.categoria))];
  const [activeCategory, setActiveCategory] = useState(null);
  const filtered = activeCategory ? posts.filter(p => p.categoria === activeCategory) : posts;

  return (
    <div className="min-h-screen bg-slate-50">
      <PageSEO path="/novita" />
      <section className="py-20 bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-6 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
              {t('novita.title')}
            </h1>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              {t('novita.subtitle')}
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-12 max-w-6xl mx-auto px-6">
        {categories.length > 1 && (
          <div className="flex flex-wrap gap-2 mb-8">
            <button
              onClick={() => setActiveCategory(null)}
              className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
                !activeCategory ? 'bg-indigo-600 text-white' : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'
              }`}
            >
              Tutte
            </button>
            {categories.map(cat => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
                  activeCategory === cat ? 'bg-indigo-600 text-white' : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        )}

        {loading ? (
          <div className="text-center py-20 text-slate-400">Caricamento...</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-slate-500 text-lg">Nessun articolo pubblicato</p>
            <p className="text-slate-400 mt-2">I nuovi articoli appariranno qui</p>
          </div>
        ) : (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {filtered.map(post => <PostCard key={post.id} post={post} />)}
          </div>
        )}
      </section>
    </div>
  );
};

const PostDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/blog/${id}`)
      .then(res => setPost(res.data))
      .catch(() => navigate('/novita'))
      .finally(() => setLoading(false));
  }, [id, navigate]);

  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-400">Caricamento...</div>;
  if (!post) return null;

  return (
    <div className="min-h-screen bg-slate-50">
      <article className="max-w-3xl mx-auto px-6 py-12">
        <Button variant="ghost" onClick={() => navigate('/novita')} className="mb-8">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Torna alle novità
        </Button>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <div className="flex items-center gap-3 mb-4">
            <Badge variant="secondary">
              <Tag className="w-3 h-3 mr-1" />
              {post.categoria}
            </Badge>
            <span className="text-sm text-slate-400 flex items-center gap-1">
              <Calendar className="w-3.5 h-3.5" />
              {new Date(post.created_at).toLocaleDateString('it-IT', { day: 'numeric', month: 'long', year: 'numeric' })}
            </span>
          </div>

          <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
            {post.titolo}
          </h1>

          <div className="bg-white rounded-xl border border-slate-200 p-8">
            <div className="prose prose-slate max-w-none whitespace-pre-line text-slate-700 leading-relaxed">
              {post.contenuto}
            </div>

            {post.allegato_file && (
              <div className="mt-6 pt-6 border-t border-slate-200">
                <a
                  href={`${API}/blog/attachment/${post.allegato_file}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-3 px-4 py-3 bg-indigo-50 hover:bg-indigo-100 border border-indigo-200 rounded-lg transition-colors group"
                  data-testid="blog-download-attachment"
                >
                  <FileText className="w-5 h-5 text-indigo-600" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-900 truncate">{post.allegato_nome}</p>
                    <p className="text-xs text-slate-500">Scarica allegato</p>
                  </div>
                  <Download className="w-4 h-4 text-indigo-600 group-hover:translate-y-0.5 transition-transform" />
                </a>
              </div>
            )}
          </div>

          <div className="mt-8 flex items-center gap-2 text-sm text-slate-400">
            <User className="w-4 h-4" />
            <span>Pubblicato da {post.autore}</span>
          </div>
        </motion.div>
      </article>
    </div>
  );
};

const NovitaPage = () => {
  const { id } = useParams();
  return id ? <PostDetail /> : <PostList />;
};

export default NovitaPage;
